/**
 * Mobile Sidebar
 *
 * Slide-in drawer for mobile viewports (< lg breakpoint).
 * Uses the same nav items and permission filtering as the desktop Sidebar.
 */

import { useMemo, useState } from 'react';
import { Link, useLocation } from '@tanstack/react-router';
import { cn } from '@/lib/utils';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useUser } from '@/stores';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { impersonation } from '@/lib/api-client';
import { useCurrentTenant } from '@/features/tenant/hooks/useTenant';
import { usePublicScoringConfig } from '@/features/crm/settings/hooks/useSettings';
import {
  navItems,
  superadminNavItems,
  adminNavItems,
  ChevronDown,
  ChevronUp,
  type NavItem,
} from './nav-items';
import { getActiveChildHref, isPathWithin, type NavigationAliases } from './navigation-state';

interface MobileSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export function MobileSidebar({ isOpen, onClose }: MobileSidebarProps) {
  const location = useLocation();
  const user = useUser();
  const displayName = user
    ? user.fullName?.trim() ||
      [user.firstName, user.lastName].filter(Boolean).join(' ').trim() ||
      user.email ||
      'User'
    : '';
  const userInitials = user
    ? `${user.firstName?.trim()?.[0] ?? ''}${user.lastName?.trim()?.[0] ?? ''}`.toUpperCase() ||
      user.email?.trim()?.[0]?.toUpperCase() ||
      'U'
    : '';
  const {
    hasPermission,
    hasAnyPermission,
    hasModuleAccess,
    hasRole,
    isSuperAdmin: isSuperAdminRole,
  } = usePermissions();
  const [expandedItemOverride, setExpandedItemOverride] = useState<{
    pathname: string;
    title: string | null;
  } | null>(null);

  const isImpersonating = impersonation.isImpersonating();
  const isSuperAdmin = !isImpersonating && isSuperAdminRole;

  const { data: tenant } = useCurrentTenant({ enabled: !isSuperAdmin });
  const tenantBranding = tenant as unknown as { logoUrl?: string; name?: string } | undefined;
  const tenantLogo = tenantBranding?.logoUrl;
  const tenantName = tenantBranding?.name ?? tenant?.name;

  // Pilot corrections §1: flagged entries (Projects) hide with the tenant feature.
  const { data: publicCrmConfig } = usePublicScoringConfig();
  const featureFlags: Record<NonNullable<NavItem['featureFlag']>, boolean> = {
    enableProjects: publicCrmConfig?.enableProjects ?? false,
  };
  // Same filtering logic as desktop Sidebar
  const filterNavItems = (items: NavItem[]): NavItem[] => {
    return items
      .filter((item) => {
        if (item.featureFlag && !featureFlags[item.featureFlag]) {
          return false;
        }
        if (item.requiredRole) {
          if (!hasRole(item.requiredRole)) return false;
        }
        if (item.module && !hasModuleAccess(item.module)) {
          return false;
        }
        if (item.permission && !hasPermission(item.permission)) {
          return false;
        }
        if (item.anyPermission && item.anyPermission.length > 0) {
          if (!hasAnyPermission(item.anyPermission)) {
            return false;
          }
        }
        return true;
      })
      .map((item) => ({
        ...item,
        children: item.children ? filterNavItems(item.children) : undefined,
      }))
      .filter((item) => {
        const originalItem = items.find((i) => i.href === item.href);
        if (originalItem?.children && originalItem.children.length > 0) {
          return item.children && item.children.length > 0;
        }
        return true;
      });
  };

  const baseNavItems = isSuperAdmin ? superadminNavItems : [...navItems, ...adminNavItems];
  const visibleNavItems = filterNavItems(baseNavItems);

  const navigationAliases = useMemo<NavigationAliases>(
    () => ({
      '/employees/me': user?.employeeId ? [`/employees/${user.employeeId}`] : [],
    }),
    [user?.employeeId],
  );
  const activeChildHref = (item: NavItem) =>
    getActiveChildHref(location.pathname, item.children, navigationAliases);
  const isChildActive = (parent: NavItem, child: NavItem) => activeChildHref(parent) === child.href;

  const activeParent = visibleNavItems.find((item) => {
    if (!item.children) return false;
    return isPathWithin(location.pathname, item.href) || Boolean(activeChildHref(item));
  });
  const expandedItem =
    expandedItemOverride?.pathname === location.pathname
      ? expandedItemOverride.title
      : (activeParent?.title ?? null);

  const toggleExpanded = (title: string) => {
    setExpandedItemOverride((prev) => {
      const currentExpanded =
        prev?.pathname === location.pathname ? prev.title : (activeParent?.title ?? null);
      return {
        pathname: location.pathname,
        title: currentExpanded === title ? null : title,
      };
    });
  };

  const isItemActive = (item: NavItem): boolean => {
    if (item.children) {
      return Boolean(activeChildHref(item)) || isPathWithin(location.pathname, item.href);
    }
    return isPathWithin(location.pathname, item.href);
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={onClose} />}

      {/* Drawer */}
      <aside
        className={cn(
          'fixed left-0 top-0 z-50 h-screen w-72 border-r bg-background transition-transform duration-300 lg:hidden flex flex-col',
          isOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        {/* Header */}
        <div className="flex h-16 items-center justify-between border-b px-4 flex-shrink-0">
          <Link to="/" className="flex items-center gap-2 min-w-0" onClick={onClose}>
            {tenantLogo ? (
              <img
                src={tenantLogo}
                alt={tenantName || 'Company logo'}
                className="w-8 h-8 rounded-lg object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-blue-400 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-white text-sm font-bold">U</span>
              </div>
            )}
            <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-blue-400 bg-clip-text text-transparent truncate">
              {tenantName || 'UniTree'}
            </span>
          </Link>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-2 space-y-1">
          {visibleNavItems.map((item) => {
            const Icon = item.icon;
            const children = item.children ?? [];
            const hasChildren = children.length > 0;
            const isActive = isItemActive(item);
            const isExpanded = expandedItem === item.title;

            if (hasChildren) {
              return (
                <div key={item.title}>
                  {/* Parent item — toggles accordion */}
                  <button
                    onClick={() => toggleExpanded(item.title)}
                    aria-expanded={isExpanded}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all hover:bg-accent',
                      isActive ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    <Icon className="h-5 w-5 flex-shrink-0" />
                    <span className="flex-1 text-left">{item.title}</span>
                    {isExpanded ? (
                      <ChevronUp className="h-4 w-4 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="h-4 w-4 flex-shrink-0" />
                    )}
                  </button>

                  {/* Children — collapsible */}
                  {isExpanded && (
                    <div className="ml-4 mt-1 space-y-1 border-l pl-3">
                      {children.map((child) => {
                        const ChildIcon = child.icon;
                        const isActiveChild = isChildActive(item, child);

                        return (
                          <Link
                            key={child.href}
                            to={child.href}
                            activeOptions={{ exact: true }}
                            onClick={onClose}
                            aria-current={isActiveChild ? 'page' : undefined}
                            className={cn(
                              'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all hover:bg-accent',
                              isActiveChild
                                ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                                : 'text-muted-foreground hover:text-foreground',
                            )}
                          >
                            <ChildIcon className="h-4 w-4 flex-shrink-0" />
                            <span className="flex-1">{child.title}</span>
                            {child.badge && (
                              <span className="ml-auto rounded-full bg-destructive px-2 py-0.5 text-xs text-destructive-foreground">
                                {child.badge}
                              </span>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            // Leaf item — direct link
            return (
              <Link
                key={item.href}
                to={item.href}
                activeOptions={{ exact: true }}
                onClick={onClose}
                aria-current={isActive ? 'page' : undefined}
                className={cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all hover:bg-accent',
                  isActive
                    ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <Icon className="h-5 w-5 flex-shrink-0" />
                <span className="flex-1">{item.title}</span>
              </Link>
            );
          })}
        </nav>

        {/* User Info */}
        {user && (
          <div className="border-t p-4 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600 to-blue-400 flex items-center justify-center text-white font-semibold">
                {userInitials}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{displayName}</p>
                <p className="text-xs text-muted-foreground truncate">{user.email}</p>
              </div>
            </div>
          </div>
        )}
      </aside>
    </>
  );
}
